import { z } from 'zod';
import { dateStringSchema, isoDateTimeSchema, nameSchema } from './common.js';

export const programSlugSchema = z.enum([
  'quran-reading',
  'tajweed',
  'hifz',
  'adult-beginners',
]);

const courseCreateBaseSchema = z
  .object({
    slug: z.string().trim().min(2).max(60).regex(/^[a-z0-9-]+$/u),
    name: nameSchema,
    program: programSlugSchema,
    description: z.string().max(4000).optional(),
    durationWeeksMin: z.number().int().min(1).max(520),
    durationWeeksMax: z.number().int().min(1).max(520),
    sessionsPerWeek: z.number().int().min(1).max(7),
    sessionMinutes: z.number().int().min(15).max(180),
  })
  .strict();

export const courseCreateSchema = courseCreateBaseSchema.refine(
  (data) => data.durationWeeksMin <= data.durationWeeksMax,
  {
    message: 'durationWeeksMin cannot exceed durationWeeksMax',
    path: ['durationWeeksMin'],
  },
);

export const courseUpdateSchema = courseCreateBaseSchema
  .partial()
  .extend({ isActive: z.boolean().optional() })
  .refine(
    (data) =>
      data.durationWeeksMin === undefined ||
      data.durationWeeksMax === undefined ||
      data.durationWeeksMin <= data.durationWeeksMax,
    {
      message: 'durationWeeksMin cannot exceed durationWeeksMax',
      path: ['durationWeeksMin'],
    },
  );

const lessonBaseSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    summary: z.string().trim().max(2000).optional(),
    orderIndex: z.number().int().min(0).optional(),
    expectedDurationMinutes: z.number().int().min(1).max(600).optional(),
  })
  .strict();

export const lessonCreateSchema = lessonBaseSchema;

export const lessonUpdateSchema = lessonBaseSchema.partial();

export const lessonsReorderSchema = z
  .object({
    lessonIds: z.array(z.string().uuid()).min(1),
  })
  .strict();

export type LessonCreateInput = z.infer<typeof lessonCreateSchema>;
export type LessonUpdateInput = z.infer<typeof lessonUpdateSchema>;
export type LessonsReorderInput = z.infer<typeof lessonsReorderSchema>;

const dayOfWeekSchema = z.enum(['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']);
const timeOfDaySchema = z.string().regex(/^\d{2}:\d{2}$/u, 'Use HH:MM 24-hour format');

const batchScheduleSlotSchema = z.object({
  day: dayOfWeekSchema,
  start: timeOfDaySchema,
  end: timeOfDaySchema,
  /**
   * Hybrid batches only: whether this class meets online or at the centre.
   * Decides which daily report checklist the teacher gets that day; weekly
   * and monthly reports use whichever mode had more classes (default online).
   */
  mode: z.enum(['online', 'offline']).optional(),
});

export const batchCreateSchema = z
  .object({
    name: nameSchema,
    courseId: z.string().uuid(),
    teacherId: z.string().uuid(),
    nalimId: z.string().uuid().optional(),
    mode: z.enum(['online', 'offline', 'hybrid']),
    startDate: dateStringSchema,
    endDate: dateStringSchema.optional(),
    schedule: z.array(batchScheduleSlotSchema).min(1).max(7),
    capacity: z.number().int().min(1).max(60),
    location: z.string().max(200).optional(),
    notes: z.string().max(2000).optional(),
  })
  .strict();

export const batchUpdateSchema = batchCreateSchema.partial().extend({
  status: z.enum(['draft', 'active', 'completed', 'archived']).optional(),
});

export const batchEnrollmentSchema = z
  .object({
    batchId: z.string().uuid(),
    studentId: z.string().uuid(),
    startDate: dateStringSchema,
  })
  .strict();

/**
 * Body schema for `POST /api/batches/:id/enrollments` — the URL carries the
 * batch id so the create payload only ships the student + start date and an
 * optional end date for planned exits. Distinct from `batchEnrollmentSchema`
 * (which is the full row identity) because that variant lacks `endDate` and
 * requires `batchId`.
 */
export const batchEnrollmentCreateSchema = z
  .object({
    studentId: z.string().uuid(),
    startDate: dateStringSchema,
    endDate: dateStringSchema.optional(),
  })
  .strict();

/**
 * Body schema for `PATCH /api/batches/:id/enrollments/:studentId`. Status is
 * required (the patch only exists to drive lifecycle transitions); `endDate`
 * is optional and the route auto-fills it when withdrawing/completing.
 */
export const batchEnrollmentUpdateSchema = z
  .object({
    status: z.enum(['active', 'paused', 'completed', 'withdrawn']),
    endDate: dateStringSchema.optional(),
  })
  .strict();

const classSessionBaseSchema = z
  .object({
    scheduledAt: isoDateTimeSchema,
    durationMinutes: z.number().int().min(5).max(480),
    location: z.string().trim().max(200).optional(),
    lessonId: z.string().uuid().optional(),
    notes: z.string().trim().max(2000).optional(),
    /** `extra` / `makeup` add a class outside the timetable (0020). */
    kind: z.enum(['regular', 'extra', 'makeup']).optional(),
    /** The teacher taking this class instead of the batch teacher. */
    substituteTeacherId: z.string().uuid().nullable().optional(),
  })
  .strict();

export const classSessionCreateSchema = classSessionBaseSchema;

export const classSessionUpdateSchema = classSessionBaseSchema.partial().extend({
  status: z
    .enum(['scheduled', 'in_progress', 'completed', 'cancelled'])
    .optional(),
  cancelReason: z.string().trim().max(500).nullable().optional(),
});

export const attendanceStatusSchema = z.enum(['present', 'absent', 'late', 'excused']);

export const attendanceRecordSchema = z
  .object({
    sessionId: z.string().uuid(),
    studentId: z.string().uuid(),
    status: attendanceStatusSchema,
    notes: z.string().max(500).optional(),
  })
  .strict();

export const attendanceBulkSchema = z
  .object({
    // The bulk-mark handler keys the records off the URL `:dayId`, never the
    // body `sessionId`. It is therefore optional (still validated as a uuid
    // when present) so the day-based portal save flow need not synthesise one.
    sessionId: z.string().uuid().optional(),
    records: z.array(attendanceRecordSchema.omit({ sessionId: true })).min(1).max(60),
    // When true, the records-save and the day-finalize commit atomically in a
    // single batch (no "records saved but day unfinalized" window). Omitting it
    // preserves the original wire contract: records are upserted, the day's
    // finalized state is left exactly as-is.
    finalize: z.boolean().optional(),
  })
  .strict();

export const weeklyTaskCreateSchema = z
  .object({
    batchId: z.string().uuid(),
    weekStartDate: dateStringSchema,
    lessonId: z.string().uuid(),
    description: z.string().min(2).max(1000),
    expectedCompletion: dateStringSchema,
  })
  .strict();

/**
 * Body schema for `PATCH /api/batches/:id/weekly-tasks/:taskId`. All fields
 * optional (partial of create) so callers can update one column at a time;
 * the route validates ownership separately (batch ↔ task linkage).
 */
export const weeklyTaskUpdateSchema = weeklyTaskCreateSchema.partial();

export const weeklyReportCreateSchema = z
  .object({
    batchId: z.string().uuid(),
    weekStartDate: dateStringSchema,
    summary: z.string().min(2).max(2000),
    challenges: z.string().max(2000).optional(),
    upcoming: z.string().max(2000).optional(),
    studentNotes: z
      .array(
        z.object({
          studentId: z.string().uuid(),
          note: z.string().min(2).max(1000),
        }),
      )
      .max(60)
      .optional(),
  })
  .strict();

const meetingReportBaseShape = {
  batchId: z.string().uuid().optional(),
  householdId: z.string().uuid().optional(),
  meetingAt: isoDateTimeSchema,
  summary: z.string().min(2).max(4000),
  attendees: z.array(z.string().min(2).max(120)).min(1).max(20),
  followUps: z.string().max(2000).optional(),
} as const;

export const meetingReportCreateSchema = z
  .object(meetingReportBaseShape)
  .strict()
  .refine((data) => Boolean(data.batchId) || Boolean(data.householdId), {
    message: 'Either batchId or householdId is required',
    path: ['batchId'],
  });

export const meetingReportUpdateSchema = z
  .object({
    batchId: z.string().uuid().nullable().optional(),
    householdId: z.string().uuid().nullable().optional(),
    meetingAt: isoDateTimeSchema.optional(),
    summary: z.string().min(2).max(4000).optional(),
    attendees: z.array(z.string().min(2).max(120)).min(1).max(20).optional(),
    followUps: z.string().max(2000).nullable().optional(),
  })
  .strict()
  /**
   * XOR refinement for re-link: only enforced when BOTH `batchId` and
   * `householdId` keys are present in the patch body. Partial updates that
   * only touch one side must not force the caller to send the other column
   * (the existing pairing is left unchanged on the server). When both are
   * present we require exactly one to resolve to a non-empty value so the
   * meeting always remains attached to either a batch or a household.
   */
  .superRefine((data, ctx) => {
    if (data.batchId !== undefined && data.householdId !== undefined) {
      const exactlyOne = Boolean(data.batchId) !== Boolean(data.householdId);
      if (!exactlyOne) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['batchId'],
          message:
            'Exactly one of batchId or householdId must be set when both are provided',
        });
      }
    }
  });

export const announcementCreateSchema = z
  .object({
    title: z.string().min(2).max(160),
    body: z.string().min(2).max(8000),
    targets: z
      .object({
        roles: z.array(z.string()).optional(),
        batchIds: z.array(z.string().uuid()).optional(),
        householdIds: z.array(z.string().uuid()).optional(),
        allUsers: z.boolean().optional(),
      })
      .refine(
        (t) =>
          (t.roles && t.roles.length > 0) ||
          (t.batchIds && t.batchIds.length > 0) ||
          (t.householdIds && t.householdIds.length > 0) ||
          t.allUsers === true,
        'At least one target is required',
      ),
    sendPush: z.boolean().default(true),
    sendEmail: z.boolean().default(false),
  })
  .strict();

/**
 * PR 15 — announcement write-path schemas.
 *
 * Editable fields are a strict subset of the create payload. Targets and
 * resend/withdraw/duplicate are separate endpoints (and separate schemas)
 * because their semantics differ enough that bundling them into a single
 * patch would force the handler to thread mutually exclusive branches.
 */
export const announcementUpdateSchema = z
  .object({
    title: z.string().min(2).max(160).optional(),
    body: z.string().min(2).max(8000).optional(),
    sendPush: z.boolean().optional(),
    sendEmail: z.boolean().optional(),
    /** Reschedule a not-yet-published announcement. ISO-8601 string. */
    scheduledFor: isoDateTimeSchema.nullable().optional(),
  })
  .strict()
  .refine(
    (data) => Object.keys(data).length > 0,
    'At least one field must be provided',
  );

export const announcementTargetTypeSchema = z.enum([
  'role',
  'batch',
  'household',
  'all_users',
]);

export const announcementTargetsReplaceSchema = z
  .object({
    targets: z
      .array(
        z
          .object({
            targetType: announcementTargetTypeSchema,
            targetValue: z.string().min(1).max(120).optional(),
          })
          .strict()
          .superRefine((target, ctx) => {
            // `all_users` is the only target type that ignores targetValue;
            // every other type must carry one (role slug / batch id /
            // household id). We enforce this on the schema instead of the
            // handler so 400s come back with structured field paths.
            if (target.targetType === 'all_users') return;
            if (!target.targetValue) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['targetValue'],
                message: 'targetValue is required for this target type',
              });
            }
          }),
      )
      .min(1),
  })
  .strict();

export const announcementResendSchema = z
  .object({
    scope: z.enum(['unread', 'all']),
  })
  .strict();

export const announcementWithdrawSchema = z
  .object({
    reason: z.string().max(2000).optional(),
  })
  .strict();

export const announcementDuplicateSchema = z
  .object({
    title: z.string().min(2).max(160).optional(),
    scheduledFor: isoDateTimeSchema.nullable().optional(),
  })
  .strict();

export const resourceUploadSchema = z
  .object({
    batchId: z.string().uuid().optional(),
    courseId: z.string().uuid().optional(),
    title: z.string().min(2).max(160),
    description: z.string().max(2000).optional(),
    mimeType: z.string().min(2).max(120),
    sizeBytes: z.number().int().positive().max(50 * 1024 * 1024),
  })
  .strict()
  .refine((data) => Boolean(data.batchId) || Boolean(data.courseId), {
    message: 'Either batchId or courseId is required',
  });

/**
 * Final resource record submitted after the client has uploaded the file
 * body to R2 via `/api/uploads/sign` + `/api/uploads/direct`. The returned
 * `r2Key` from the upload flow is what binds the metadata to the bytes.
 *
 * Split into a strict base object (so we can derive a `.partial()` patch
 * schema) and a refinement wrapper. Mirrors the `courseCreateBaseSchema`
 * pattern earlier in this file — `ZodEffects` (the output of `.refine()`)
 * does not expose `.partial()`, so the base must be the unwrapped object.
 */
const resourceCreateBaseSchema = z
  .object({
    r2Key: z.string().trim().min(3).max(512),
    title: z.string().trim().min(2).max(160),
    description: z.string().max(2000).optional(),
    mimeType: z.string().trim().min(2).max(120),
    sizeBytes: z.number().int().positive().max(50 * 1024 * 1024),
    batchId: z.string().uuid().optional(),
    courseId: z.string().uuid().optional(),
  })
  .strict();

export const resourceCreateSchema = resourceCreateBaseSchema.refine(
  (data) => Boolean(data.batchId) || Boolean(data.courseId),
  {
    message: 'Either batchId or courseId is required',
    path: ['batchId'],
  },
);

/**
 * Body schema for `PATCH /api/resources/:id`. All fields optional so callers
 * can update any subset; `batchId` / `courseId` accept `null` so re-scope can
 * clear a side. Cross-field XOR is enforced **only** when both keys appear
 * in the patch body — partial updates that touch one side leave the existing
 * pairing in place and must not require the caller to echo the other column.
 */
export const resourceUpdateSchema = resourceCreateBaseSchema
  .partial()
  .extend({
    batchId: z.string().uuid().nullable().optional(),
    courseId: z.string().uuid().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.batchId !== undefined && data.courseId !== undefined) {
      const exactlyOne = Boolean(data.batchId) !== Boolean(data.courseId);
      if (!exactlyOne) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['batchId'],
          message:
            'Exactly one of batchId or courseId must be set when both are provided',
        });
      }
    }
  });

export const progressStatusSchema = z.enum([
  'not_started',
  'in_progress',
  'completed',
  'needs_review',
]);

export const progressEntriesSchema = z
  .object({
    entries: z
      .array(
        z
          .object({
            lessonId: z.string().uuid(),
            status: progressStatusSchema,
            notes: z.string().max(2000).optional(),
          })
          .strict(),
      )
      .min(1)
      .max(200),
  })
  .strict();

export type BatchCreateInput = z.infer<typeof batchCreateSchema>;
export type BatchUpdateInput = z.infer<typeof batchUpdateSchema>;
export type AttendanceBulkInput = z.infer<typeof attendanceBulkSchema>;
export type WeeklyReportInput = z.infer<typeof weeklyReportCreateSchema>;
export type AnnouncementInput = z.infer<typeof announcementCreateSchema>;
export type AnnouncementUpdateInput = z.infer<typeof announcementUpdateSchema>;
export type AnnouncementTargetsReplaceInput = z.infer<
  typeof announcementTargetsReplaceSchema
>;
export type AnnouncementResendInput = z.infer<typeof announcementResendSchema>;
export type AnnouncementWithdrawInput = z.infer<typeof announcementWithdrawSchema>;
export type AnnouncementDuplicateInput = z.infer<
  typeof announcementDuplicateSchema
>;
export type CourseCreateInput = z.infer<typeof courseCreateSchema>;
export type CourseUpdateInput = z.infer<typeof courseUpdateSchema>;
export type MeetingReportCreateInput = z.infer<typeof meetingReportCreateSchema>;
export type MeetingReportUpdateInput = z.infer<typeof meetingReportUpdateSchema>;
export type ResourceCreateInput = z.infer<typeof resourceCreateSchema>;
export type ResourceUpdateInput = z.infer<typeof resourceUpdateSchema>;
export type ProgressEntriesInput = z.infer<typeof progressEntriesSchema>;
export type BatchEnrollmentCreateInput = z.infer<typeof batchEnrollmentCreateSchema>;
export type BatchEnrollmentUpdateInput = z.infer<typeof batchEnrollmentUpdateSchema>;
export type ClassSessionCreateInput = z.infer<typeof classSessionCreateSchema>;
export type ClassSessionUpdateInput = z.infer<typeof classSessionUpdateSchema>;
export type WeeklyTaskCreateInput = z.infer<typeof weeklyTaskCreateSchema>;
export type WeeklyTaskUpdateInput = z.infer<typeof weeklyTaskUpdateSchema>;
